import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { analysisApi, userApi } from '../services/api';

const DashboardPage: React.FC = () => {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['userStats'],
    queryFn: () => userApi.getStats().then((r) => r.data),
  });

  const { data: analysisStats } = useQuery({
    queryKey: ['analysisStats'],
    queryFn: () => analysisApi.getStats().then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-500 dark:text-gray-400">Your writing statistics</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Analyses</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {stats?.total_analyses || 0}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">Total Corrections</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {stats?.total_edits || 0}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">Monthly Usage</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {stats?.monthly_usage || 0} / {stats?.monthly_limit || '∞'}
          </div>
        </div>
        <div className="card p-6">
          <div className="text-sm text-gray-500 dark:text-gray-400">Tier</div>
          <div className="text-3xl font-bold text-gray-900 dark:text-white mt-1">
            {stats?.tier_name || 'Free'}
          </div>
        </div>
      </div>

      {/* Common errors */}
      {analysisStats?.common_errors?.length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Most Common Errors
          </h2>
          <div className="space-y-3">
            {analysisStats.common_errors.slice(0, 5).map((error: any) => (
              <div key={error.code} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700">
                    {error.code}
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">{error.code}</span>
                </div>
                <span className="text-gray-900 dark:text-white font-medium">{error.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error breakdown */}
      {stats?.error_breakdown && Object.keys(stats.error_breakdown).length > 0 && (
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Error Distribution
          </h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.error_breakdown)
              .sort((a: any, b: any) => b[1] - a[1])
              .slice(0, 10)
              .map(([code, count]: [string, any]) => (
                <span
                  key={code}
                  className="px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                >
                  {code}: {count}
                </span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
